import React, {useState, useEffect} from 'react';
import { Text, TouchableOpacity, View, StyleSheet, ActivityIndicator } from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import I18n from 'i18n-js';
import { useDispatch, useSelector } from 'react-redux';
import { SearchScanpackOrder } from '../../actions/scanpackAction';
import { handleIsEmptyCart } from '../../actions/ScanToCartScanningActions';
import { navigate } from '../root_navigation';
import { canShowProcessNowButton, getProcessNowConfig } from '../scanToCartScanning/utils/scanningUtils';
import { getPostScanStepsFromConfigs } from '../../utils/postScanning/postScanConfig';
import { sharedPromptStyles, basePromptContainer, baseResponseView, baseTitleText, baseButtonText, baseActionButton } from './shared/promptStyles';

const CartAssignmentPrompt = ({showPrompt,setShowPrompt,cartId, payload}) => {
  const [tooltips, setTooltips] = useState({
    continueTooltip: false,
    restartTooltip: false,
    cancelTooltip: false,
    processNowTooltip: false
  });

  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] =  useState('');

  const {isVisible, type} = useSelector(
      state => state.scanToCartScanning.prompt
    );
  const isProcessing = isVisible && type === 'reprint';

  const dispatch = useDispatch();

  const {
    toteAssignments,
    cartOrders,
    lastCartInput,
    scanPackSettings,
    settings
  } = useSelector(state => ({
    toteAssignments: state.scanToCart.toteAssignments,
    cartOrders: state.scanToCart.cartOrders,
    lastCartInput: state.scanToCartScanning.lastCartInput,
    scanPackSettings: state.user.bothSettings?.data?.data?.scanpack_setting,
    settings: state.user.settings,
  }));

  const shouldShowProcessNowButton = canShowProcessNowButton(
    settings,
    scanPackSettings,
    toteAssignments,
    cartOrders,
    lastCartInput
  );

  const HandleCartAssignmentAction = (action) => {
    if(action === 'take_assignment' && isProcessing) {
      navigate('ScanToCart');
      return;
    }
    const data = {
      ...payload,
      input: cartId,
      cart_action: action
    };
    setIsLoading(true);
    dispatch(SearchScanpackOrder(data, callBack))
    dispatch(handleIsEmptyCart(false))
  }

  const callBack = (cartId, message) => {
    setIsLoading(false);
    setMessage(message);
    setTimeout(() => {
      handleClosePrompt();
    }, 3000);
  }


  const toggleTooltip = tooltipName => {
    setTooltips({
      continueTooltip:
        tooltipName === 'continue' ? !tooltips.continueTooltip : false,
      restartTooltip:
        tooltipName === 'restart' ? !tooltips.restartTooltip : false,
      cancelTooltip: tooltipName === 'cancel' ? !tooltips.cancelTooltip : false,
      processNowTooltip:
        tooltipName === 'processNow' ? !tooltips.processNowTooltip : false
    });
  };

  useEffect(() => {
    const handleClickOutside = () => {
      if (
        tooltips.continueTooltip ||
        tooltips.restartTooltip ||
        tooltips.cancelTooltip ||
        tooltips.processNowTooltip
      ) {
        setTooltips({
          continueTooltip: false,
          restartTooltip: false,
          cancelTooltip: false,
          processNowTooltip: false
        });
      }
    };

    if (
      tooltips.continueTooltip ||
      tooltips.restartTooltip ||
      tooltips.cancelTooltip
    ) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tooltips]);

  const GradientButton = ({text, onPress, testID}) => (
    <TouchableOpacity testID={testID} onPress={onPress}>
      <LinearGradient
        locations={[0, 0.7, 1]}
        start={{x: 0, y: 0}}
        end={{x: 0, y: 1}}
        colors={['#ffb636', '#ffb636', '#d47d02']}
        style={styles.actionButton}>
        <Text style={styles.buttonText}>{text}</Text>
      </LinearGradient>
    </TouchableOpacity>
  );

  const TooltipComponent = ({isVisible, tooltipId, content}) => {
    if (!isVisible) return null;

    return (
      <LinearGradient
        testID={tooltipId}
        locations={[0, 1]}
        colors={['#8a9daf', '#d7e1e9']}
        style={styles.tooltipContainer}>
        <Text style={styles.tooltipText}>{content}</Text>
        <View style={styles.tooltipArrow} />
      </LinearGradient>
    );
  };

  const ButtonWithTooltip = ({type, onPress = () => {}}) => {
    const tooltipKey = `${type}Tooltip`;
    const isTooltipVisible = tooltips[tooltipKey];
  
    return (
      <View style={styles.buttonWithTooltip}>
        <View style={styles.buttonRow}>
          <GradientButton
            testID={`${type}Button`}
            text={I18n.t(`cartAssignment.${type}Button`)}
            onPress={onPress}
          />
          <TouchableOpacity
            testID={`${type}InfoIcon`}
            style={styles.infoIconWrapper}
            onPress={() => toggleTooltip(type)}>
            <i className="icon-info-circled" style={styles.infoIcon}></i>
          </TouchableOpacity>
        </View>
        <TooltipComponent
          isVisible={isTooltipVisible}
          tooltipId={`${type}Tooltip`}
          content={I18n.t(`cartAssignment.${type}Tooltip`)}
        />
      </View>
    );
  };

  const handleProcessNow = () => {
    const processNowConfig = getProcessNowConfig(toteAssignments, lastCartInput);
    if (!processNowConfig) return;
    const { orderId, toteInput } = processNowConfig;
    const { steps } = getPostScanStepsFromConfigs(
      settings?.data?.post_scanning_config,
      scanPackSettings
    );
    setShowPrompt(false);
    dispatch(handleIsEmptyCart(false));

    if (steps.length) {
      navigate('ScanToCart', {
        postScanningContinue: true,
        postScanOrderId: orderId,
        postScanningModulesRemaining: steps,
        postScanningToteInput: toteInput || null
      });
      return;
    }

    const input = toteInput || lastCartInput;
    if (!input) {
      return;
    }
    const payload = {
      app: 'app',
      input,
      scan_to_cart_enabled: true,
      state: 'scanpack.rfo'
    };
    setIsLoading(true);
    dispatch(SearchScanpackOrder(payload, () => setIsLoading(false)));
  };

  const buttons = [
    {type: `${isProcessing ? 'continueProcessing' : 'continue'}`, onPress: () => HandleCartAssignmentAction('take_assignment')},
    {type: 'restart', onPress: () => HandleCartAssignmentAction('restart')},
    {type: 'cancel', onPress: () => setShowPrompt(false)},
    ...(shouldShowProcessNowButton
      ? [{ type: 'processNow', onPress: handleProcessNow }]
      : [])
  ];

  const handleClosePrompt = () => {
    setShowPrompt(false);
    setMessage('');
  }

  return (
    <View testID="cartAssignmentPrompt" style={[styles.responseView, {display: !showPrompt && 'none' }]}>
      <LinearGradient
        locations={[0, 1]}
        start={{x: 0, y: 1}}
        end={{x: 0, y: 0}}
        colors={['#d47d02', '#ffb636']}
        style={styles.promptContainer}>
        <View style={styles.cancelIcon}>
          <TouchableOpacity testID="CancelIcon" onPress={handleClosePrompt}>
            <i
              className="icon-cancel"
              style={{fontSize: 26, color: '#1C1C1C'}}></i>
          </TouchableOpacity>
        </View>

        <View style={styles.titleContainer}>
          <Text style={styles.titleText}>{I18n.t('cartAssignment.title')}</Text>
        </View>

        <View style={styles.buttonContainer}>
          {buttons.map(button => (
            <ButtonWithTooltip
              key={button.type}
              type={button.type}
              onPress={button.onPress}
            />
          ))}
        </View>
        <View>
          {isLoading && <ActivityIndicator size="large" color="#000"/>}
          {message?.length > 0 && <Text style={styles.titleText}>{message}</Text>}
        </View>
      </LinearGradient>
    </View>
  );
};

const styles = StyleSheet.create({
  responseView: {
    ...baseResponseView,
    justifyContent: 'flex-start',
    top: 0,
    zIndex: 1000,
    paddingTop: 40,
    paddingBottom: 40,
  },
  promptContainer: {
    ...basePromptContainer,
    maxWidth: 550,
  },
  cancelIcon: sharedPromptStyles.cancelIcon,
  titleContainer: sharedPromptStyles.titleContainer,
  titleText: baseTitleText,
  buttonContainer: sharedPromptStyles.buttonContainer,
  buttonWithTooltip: {
    width: '100%',
    position: 'relative',
    marginBottom: 5
  },
  buttonRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    marginBottom: 5
  },
  actionButton: {
    ...baseActionButton,
    paddingVertical: 8,
    minWidth: '75%',
  },
  buttonText: baseButtonText,
  infoIconWrapper: {
    marginLeft: 10,
    padding: 5
  },
  infoIcon: {
    color: '#1C1C1C',
    fontSize: 24
  },
  tooltipContainer: {
    padding: 12,
    borderRadius: 5,
    marginTop: 5,
    width: '100%',
    position: 'relative',
    zIndex: 2
  },
  tooltipText: {
    color: '#000',
    fontFamily: 'Poppins_300Light',
    fontSize: 14,
    lineHeight: 20
  },
  tooltipArrow: {
    position: 'absolute',
    top: -10,
    left: '45%',
    width: 0,
    height: 0,
    borderLeftWidth: 10,
    borderRightWidth: 10,
    borderBottomWidth: 10,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: '#8a9daf'
  }
});

export default CartAssignmentPrompt;
